import { HelpCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { HELP_TOPICS } from "../../data/helpContent";

/**
 * Icone d'aide contextuelle (?) a placer a cote des sections complexes.
 *
 * Usage :
 *   <HelpIcon topic="ppf_factur_x" />
 *   <HelpIcon topic="offers_builder" size="md" />
 *   <HelpIcon topic="portage" inline label="En savoir plus" />
 *
 * Comportement :
 *   - Hover/click → popover avec titre + extrait de la 1ere section
 *   - Bouton "Ouvrir le centre d'aide" → navigate vers /aide?topic=xxx
 */
export default function HelpIcon({
  topic,
  size = "sm",
  inline = false,
  label = null,
  className = "",
}) {
  const navigate = useNavigate();
  const topicData = HELP_TOPICS[topic];

  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  const iconSize = sizeClasses[size] || sizeClasses.sm;

  // Si topic inexistant, on retourne juste un lien vers /aide
  if (!topicData) {
    return (
      <button
        onClick={() => navigate("/aide")}
        className={`text-slate-500 hover:text-[#f7c935] transition-colors ${className}`}
        title="Centre d'aide"
        data-testid={`help-icon-fallback-${topic}`}
      >
        <HelpCircle className={iconSize} />
      </button>
    );
  }

  const firstSection = topicData.sections?.[0];
  const previewText = firstSection?.body?.slice(0, 200) || "";

  const trigger = (
    <button
      type="button"
      className={`inline-flex items-center gap-1 text-slate-500 hover:text-[#f7c935] transition-colors ${className}`}
      data-testid={`help-icon-${topic}`}
      aria-label={`Aide : ${topicData.title}`}
    >
      <HelpCircle className={iconSize} />
      {inline && label && <span className="text-[10px] font-medium underline-offset-2 hover:underline">{label}</span>}
    </button>
  );

  return (
    <Popover>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        {trigger}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-80 bg-[#0b0811] border-white/10 text-white p-4 z-50"
        data-testid={`help-popover-${topic}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-[#f7c935]/15 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="w-3.5 h-3.5 text-[#f7c935]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-white leading-tight">{topicData.title}</h4>
            <p className="text-[10px] text-slate-500 mt-0.5">Aide contextuelle</p>
          </div>
        </div>

        {firstSection && (
          <div className="mb-3">
            <h5 className="text-[10px] uppercase font-bold text-[#f7c935] tracking-wider mb-1">{firstSection.title}</h5>
            <p className="text-xs text-slate-300 leading-relaxed">
              {previewText}
              {firstSection.body?.length > 200 ? "..." : ""}
            </p>
          </div>
        )}

        {topicData.sections?.length > 1 && (
          <p className="text-[10px] text-slate-500 mb-3">
            + {topicData.sections.length - 1} autres sections (Inviter, Filtrer, Supprimer, etc.)
          </p>
        )}

        <button
          onClick={() => navigate(`/aide?topic=${topic}`)}
          className="w-full text-xs px-3 py-1.5 rounded-lg bg-[#f7c935]/15 text-[#f7c935] font-bold hover:bg-[#f7c935]/25 transition-colors flex items-center justify-center gap-1.5"
          data-testid={`help-popover-link-${topic}`}
        >
          Ouvrir le centre d'aide <ExternalLink className="w-3 h-3" />
        </button>
      </PopoverContent>
    </Popover>
  );
}
